import store from "../store/index";
import tenant from "./tenant";
class TimeTracking {
  constructor(storageKey = "timeTrackingEntries") {
    this.storageKey = storageKey;
    this.activeSession = null;
  }

  start({
    event = null,
    orderId = null,
    productVariantId = null,
    warehouseOperationId = null,
  }) {
    const now = new Date();

    if (this.activeSession) {
      this.stop();
    }

    this.activeSession = {
      event,
      orderId,
      productVariantId,
      warehouseOperationId,
      start: now.toISOString(),
    };
  }

  stop() {
    if (!this.activeSession) return null;

    const now = new Date();

    const entry = {
      ...this.activeSession,
      end: now.toISOString(),
      duration: now - new Date(this.activeSession.start),
      synced: false,
    };

    this._saveEntry(entry);
    this.activeSession = null;

    return entry;
  }

  getActive() {
    return this.activeSession;
  }

  isTracking() {
    return this.activeSession !== null;
  }

  getAllEntries() {
    return this._loadEntries();
  }

  getUnsyncedEntries() {
    return this._loadEntries().filter((e) => !e.synced);
  }

  markAsSynced(entryIds) {
    const entries = this._loadEntries();

    const updated = entries.map((entry, index) => {
      if (entryIds.includes(index)) {
        return { ...entry, synced: true };
      }
      return entry;
    });

    this._storeEntries(updated);
  }

  clearSynced() {
    const entries = this._loadEntries();

    const remaining = entries.filter((entry) => !entry.synced);

    this._storeEntries(remaining);

    return {
      removed: entries.length - remaining.length,
      remaining: remaining.length,
    };
  }

  clearAll() {
    localStorage.removeItem(this.storageKey);
  }

  async syncEntries({ useBeacon = false } = {}) {
    if (!navigator.onLine) {
      return { success: false, syncedCount: 0 };
    }

    const entries = this._loadEntries();
    const unsyncedIndexes = [];
    const unsyncedEntries = [];

    entries.forEach((entry, index) => {
      if (!entry.synced) {
        unsyncedIndexes.push(index);
        unsyncedEntries.push(entry);
      }
    });

    if (!unsyncedEntries.length) {
      return { success: true, syncedCount: 0 };
    }

    const endpoint = tenant.getApiBaseUrl() + "/work/time-tracking";

    try {
      if (useBeacon) {
        const queued = navigator.sendBeacon(
          endpoint,
          new Blob([JSON.stringify({ entries: unsyncedEntries })], {
            type: "application/json",
            Accept: "application/json",
            Authorization: "Bearer " + store.state.token,
          })
        );

        if (!queued) {
          return { success: false, syncedCount: 0 };
        }

        // Beacon queued successfully (not confirmed delivered!)
        this._markIndexesAsSynced(entries, unsyncedIndexes);

        return {
          success: true,
          syncedCount: unsyncedEntries.length,
        };
      }

      // Normal fetch
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: "Bearer " + store.state.token,
        },
        body: JSON.stringify({ entries: unsyncedEntries }),
      });

      if (!response.ok) {
        throw new Error("Sync failed");
      }

      this._markIndexesAsSynced(entries, unsyncedIndexes);

      return {
        success: true,
        syncedCount: unsyncedEntries.length,
      };
    } catch (error) {
      console.error("TimeTracking sync failed:", error);
      return { success: false, syncedCount: 0 };
    }
  }

  _markIndexesAsSynced(entries, indexes) {
    const updated = entries.map((entry, index) => {
      if (indexes.includes(index)) {
        return { ...entry, synced: true };
      }
      return entry;
    });

    this._storeEntries(updated);
  }
  _saveEntry(entry) {
    const entries = this._loadEntries();
    entries.push(entry);
    this._storeEntries(entries);
  }

  _loadEntries() {
    try {
      return JSON.parse(localStorage.getItem(this.storageKey)) || [];
    } catch {
      return [];
    }
  }

  _storeEntries(entries) {
    localStorage.setItem(this.storageKey, JSON.stringify(entries));
  }
}

// Singleton export
export default new TimeTracking();
